---
meta:
  - title: Como crear un playbook - IUSPRO Docs
  - description: Aprende a crear playbooks.
---

# Como crear un playbook

Un playbook define las reglas que debe cumplir un tipo de documento. Una vez creado, puede usarse para revisar múltiples documentos automáticamente.

## Estructura de un playbook

Un playbook está compuesto de **reglas**. Cada regla verifica un aspecto específico del documento.

## Como crear reglas

### 1. Nombre de la regla

Dele un nombre descriptivo que indique qué verifica. Por ejemplo:

- "Identificación de partes y representantes"
- "Hechos enumerados claramente"
- "Fundamentos de derecho"
- "Pretensión principal"

### 2. Posición inicial

Defina qué debe cumplirse idealmente. Sea específico.

**Ejemplo para "Identificación de partes"**:

```
Tiene que indicar el nombre, cualidades, número de identificación,
domicilio, e identificar al representante legal con sus datos completos.
```

### 3. Alternativa (opcional)

Defina qué es aceptable si no se cumple la posición inicial.

**Ejemplo**:

```
Alternativa: La identificación puede hacerse con pasaporte
en lugar de cédula.
```

### 4. No aceptable (opcional)

Por defecto, si no se cumple la posición inicial significa que no es aceptable. Pero puede aclarar casos específicos si es necesario.

## Principios para crear buenas reglas

### Una regla = Un tema específico

Cada regla debe enfocarse en un solo aspecto del documento. No mezcle múltiples requisitos en una sola regla.

❌ **Mal**: "Identificación y pretensiones y fundamentos"
✅ **Bien**: Tres reglas separadas para cada tema

### Sea claro y específico

Describa exactamente qué debe cumplirse. Mientras más claro sea, mejor funcionará la ejecución.

### Use ejemplos cuando ayude

Si es útil, puede incluir ejemplos de qué sí cumple y qué no cumple la regla.

## Ejemplo completo de playbook

**Playbook**: Requisitos mínimos de demanda con inicio administrativo

### Regla 1: Identificación de partes y representantes

- **Posición inicial**: Indicar nombre, cualidades, número de identificación, domicilio del demandante y demandado. Identificar representante legal con datos completos.
- **Alternativa**: Puede identificarse con pasaporte en lugar de cédula
- **No aceptable**: (dejar en blanco, se usa el default)

### Regla 2: Hechos

- **Posición inicial**: Los hechos deben estar enumerados de manera clara y en orden cronológico
- **Alternativa**: (dejar en blanco)
- **No aceptable**: (dejar en blanco)

### Regla 3: Fundamentos de derecho

- **Posición inicial**: Debe incluir la base legal con citas específicas a artículos y leyes aplicables
- **Alternativa**: (dejar en blanco)
- **No aceptable**: (dejar en blanco)

### Regla 4: Pretensión

- **Posición inicial**: La pretensión principal debe estar claramente indicada y ser específica
- **Alternativa**: (dejar en blanco)
- **No aceptable**: (dejar en blanco)

## Cuántas reglas crear

No hay límite. Cree tantas reglas como aspectos importantes tenga que verificar ese tipo de documento.

Un playbook puede tener:
- 5 reglas básicas
- 20 reglas detalladas
- O las que necesite

## Guardar el playbook

Una vez creadas todas las reglas, guarde el playbook con un nombre descriptivo:

- "Requisitos mínimos de demanda"
- "Contratos laborales"
- "Verificación de escrituras"

## Modificar un playbook

Puede editar las reglas de un playbook en cualquier momento para mejorarlo o actualizarlo.

## Próximos pasos

- Aprenda a [importar un playbook existente](/guias/revisiones-importar-manual)
- Aprenda a [compartir el playbook con otros usuarios](/guias/revisiones-compartir-manual)
